// Shared frontend types for the knowledge-area hierarchy. Mirror the backend
// AreaRecord (backend/api/knowledge/area-store.ts) with dates as ISO strings.

export const AREA_TYPES = [
  'DOMAIN',
  'MODULE',
  'SUBMODULE',
  'CAPABILITY',
  'DATA_DOMAIN',
  'DATASET',
  'REPORTING_AREA',
  'INTEGRATION',
  'OTHER',
] as const
export type AreaType = (typeof AREA_TYPES)[number]

export type AreaStatus = 'proposed' | 'active' | 'archived' | 'rejected'

export interface AreaConsolidationStatus {
  runId: number
  phase: string
  status: string
  moduleProposal?: ModuleProposal | null
}

export interface ConsolidationRunWithGaps {
  gaps?: Array<{ displayKey: string | null; question: string }>
}

export interface ModuleCandidate {
  name: string
  aliases: string[]
  confidence: 'high' | 'medium' | 'low'
  rationale: string
}

export interface ProposedModule extends ModuleCandidate {
  tempId: string
  action: 'keep' | 'add' | 'rename'
  matchedAreaId?: number
}

export interface ModuleProposal {
  domainAreaId: number
  modules: ProposedModule[]
  summary: { existing: number; add: number; rename: number }
}

export interface KnowledgeArea {
  id: number
  projectId: number
  parentId: number | null
  name: string
  canonicalName: string
  areaType: string
  description: string | null
  aliases: string[]
  status: string
  source: string
  position: number
  path: string
  depth: number
  briefStatus: string
  briefVersion: number
  lastConsolidatedAt: string | null
  /** Present while a consolidation run for this area is pending or running. */
  consolidation: AreaConsolidationStatus | null
  createdAt: string
  updatedAt: string
}

/** Human labels for consolidation run phases (backend ConsolidationPhase). */
export const CONSOLIDATION_PHASE_LABELS: Record<string, string> = {
  queued: 'Queued',
  resolving: 'Resolving context',
  retrieving: 'Retrieving evidence',
  extracting: 'Extracting knowledge',
  normalizing: 'Normalizing',
  classifying: 'Classifying',
  relating: 'Linking relationships',
  reconciling: 'Reconciling',
  covering: 'Checking coverage',
  synthesizing: 'Writing brief',
  persisting: 'Saving',
  done: 'Finishing up',
}

export function consolidationPhaseLabel(phase: string): string {
  return CONSOLIDATION_PHASE_LABELS[phase] ?? phase
}

export interface AreaTreeNode {
  area: KnowledgeArea
  children: AreaTreeNode[]
}

/** Area types whose drill-down uses the data-centric tab set (Plan 2 §26). */
export const DATA_AREA_TYPES: ReadonlySet<string> = new Set(['DATASET', 'DATA_DOMAIN'])
